import { apiGet, apiPost } from './client';
import { AccountLockedError, ApiError, RateLimitError } from './errors';

function errorWithCause(message: string, cause: unknown): Error {
  return new Error(message, { cause });
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  onboardingStatus: string;
}

/**
 * Authenticates a user with email and password.
 * Backend returns a cookie that is automatically stored and sent with subsequent requests.
 * @throws Error with user-friendly message on failure
 */
export async function login(credentials: LoginRequest): Promise<void> {
  try {
    await apiPost<void, LoginRequest>('/api/auth/login', credentials);
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.isUnauthorized || error.status === 403) {
        throw errorWithCause('Invalid email or password. Please try again.', error);
      }

      if (error.status === 429) {
        const data = error.data as { retry_after_seconds?: number } | undefined;
        const retryAfter = data?.retry_after_seconds ?? 60;
        throw new RateLimitError(
          error.message || 'Too many login attempts. Please wait before trying again.',
          retryAfter
        );
      }

      if (error.status === 423) {
        const data = error.data as { locked_until?: string } | undefined;
        throw new AccountLockedError(
          error.message || 'Account temporarily locked. Check your email for unlock instructions.',
          data?.locked_until ?? ''
        );
      }

      if (error.status >= 500) {
        throw errorWithCause('Server error. Please try again later.', error);
      }

      if (error.message) {
        throw errorWithCause(error.message, error);
      }
    }

    // Extract meaningful error messages from API response
    if (error instanceof Error) {
      // Check if it's a network error
      if (error.message.includes('Failed to fetch')) {
        throw errorWithCause(
          'Unable to connect to the server. Please check your internet connection.',
          error
        );
      }

      if (error.message.trim().length > 0) {
        throw errorWithCause(error.message, error);
      }
    }

    // Generic error fallback
    throw errorWithCause('Login failed. Please try again.', error);
  }
}

/**
 * Fetches the current authenticated user from the backend using the auth cookie.
 * Expected response shapes:
 * - { user: { ... } }
 * - { data: { user: { ... } } }
 * - { ...user fields... }
 */
export async function fetchCurrentUser(): Promise<User> {
  const response = await apiGet<User | { user: User }>('/api/auth/me');
  const user =
    response && typeof response === 'object' && 'user' in response
      ? response.user
      : (response as User);

  // v2 /auth/me doesn't include onboardingStatus — fetch it separately
  if (!user.onboardingStatus) {
    try {
      const status = await apiGet<{ status: string }>('/api/onboarding/status');
      user.onboardingStatus = status.status;
    } catch {
      // Default to completed to avoid redirect loops
      user.onboardingStatus = 'completed';
    }
  }

  return user;
}

/**
 * Registers a new user with name, email, and password.
 * Backend returns a cookie that is automatically stored and sent with subsequent requests.
 * @throws Error with user-friendly message on failure
 */
export async function register(credentials: RegisterRequest): Promise<void> {
  try {
    await apiPost<void, RegisterRequest>('/api/auth/register', credentials);
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 409) {
        throw errorWithCause(
          'This email is already registered. Please use a different email.',
          error
        );
      }

      if (error.status === 400) {
        throw errorWithCause(
          error.message || 'Invalid registration data. Please check your inputs.',
          error
        );
      }

      if (error.status >= 500) {
        throw errorWithCause('Server error. Please try again later.', error);
      }

      if (error.message) {
        throw errorWithCause(error.message, error);
      }
    }

    // Extract meaningful error messages from API response
    if (error instanceof Error) {
      // Check if it's a network error
      if (error.message.includes('Failed to fetch')) {
        throw errorWithCause(
          'Unable to connect to the server. Please check your internet connection.',
          error
        );
      }

      if (error.message.trim().length > 0) {
        throw errorWithCause(error.message, error);
      }
    }

    // Generic error fallback
    throw errorWithCause('Registration failed. Please try again.', error);
  }
}

/**
 * Unlocks a locked account using the token received by email.
 * @throws Error with user-friendly message on failure
 */
export async function unlockAccount(token: string, userId: string): Promise<void> {
  try {
    await apiGet<void>(
      `/api/unlock?token=${encodeURIComponent(token)}&user=${encodeURIComponent(userId)}`
    );
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 400) {
        throw errorWithCause(error.message || 'Unlock link is invalid or has expired.', error);
      }
      if (error.status >= 500) {
        throw errorWithCause('Server error. Please try again later.', error);
      }
    }
    if (error instanceof Error && error.message.includes('Failed to fetch')) {
      throw errorWithCause(
        'Unable to connect to the server. Please check your internet connection.',
        error
      );
    }
    throw errorWithCause('Failed to unlock account. Please try again.', error);
  }
}

/**
 * Logs out the current user.
 * Clears the authentication cookie on the backend.
 */
export async function logout(): Promise<void> {
  try {
    await apiPost('/api/auth/logout');
  } catch (error) {
    // Logout should be silent - even if it fails on the server,
    // we still want to clear client-side auth state
  }
}
